import type { Color as ColorValue } from '@backend/lib/db/typeRoots/color'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { cn } from '@/lib/utils'
import { useCanvasStore } from '../canvas-store'

// Note-friendly background tints. Light enough that the luminance-picked dark text stays
// readable; decorative user picks, so hardcoded (not theme tokens). Transparency is done
// via the custom picker's opacity slider, so there's no separate "no fill" tile.
const PRESETS = [
  '#ffffff',
  '#f3f4f6',
  '#fde68a',
  '#fdba74',
  '#fca5a5',
  '#bbf7d0',
  '#a5f3fc',
  '#bfdbfe',
  '#ddd6fe',
  '#fbcfe8',
]

function normalizeHex(hex: string | null): string | null {
  if (!hex) return null
  let h = hex.trim().toLowerCase()
  if (/^#[0-9a-f]{3}$/.test(h)) {
    h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`
  }
  // Drop a fully-opaque alpha so #rrggbbff matches the #rrggbb presets.
  if (/^#[0-9a-f]{8}$/.test(h) && h.slice(7) === 'ff') h = h.slice(0, 7)
  return h
}

// Fill menu that floats above a selected Note/Instruction card. Preset swatches + a
// no-fill tile commit directly; the last swatch opens the full color picker for a custom
// color. Lives here instead of the annotation popover (a PM request).
export function NoteFillMenu({
  effectiveHex,
  onSelect,
  pickerValue,
  onPickerChange,
}: {
  effectiveHex: string | null
  onSelect: (hex: string) => void
  pickerValue: ColorValue
  onPickerChange: (color: ColorValue, isHoverSelection?: boolean) => void
}) {
  // Counter-scale by 1/zoom so the menu keeps a constant screen size instead of shrinking /
  // growing with the canvas (the card lives in the zoomed world). left-1/2 + translateX(-50%)
  // centers it on the card width; bottom-full floats it above the card regardless of height.
  const scale = useCanvasStore((s) => s.scale)
  const norm = normalizeHex(effectiveHex)
  const isCustom = !!norm && !PRESETS.some((p) => normalizeHex(p) === norm)
  const activeRing = 'ring-primary ring-2'

  return (
    <div
      // Keep clicks off the canvas (no marquee/pan) and off the card's drag handler.
      onPointerDown={(e) => e.stopPropagation()}
      className="bg-card/95 border-border/60 absolute bottom-full left-1/2 z-10 mb-2 flex w-max items-center gap-1 rounded-full border p-1.5 shadow-lg backdrop-blur"
      style={{
        transform: `translateX(-50%) scale(${1 / scale})`,
        transformOrigin: 'center bottom',
      }}>
      {PRESETS.map((hex) => (
        <button
          key={hex}
          type="button"
          onClick={() => onSelect(hex)}
          aria-label={hex}
          title={hex}
          className={cn(
            'border-border/60 h-5 w-5 shrink-0 rounded-full border',
            normalizeHex(hex) === norm && activeRing,
          )}
          style={{ backgroundColor: hex }}
        />
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Custom color"
            title="Custom color"
            className={cn('h-5 w-5 shrink-0 rounded-full', isCustom && activeRing)}
            style={{ background: 'conic-gradient(red,yellow,lime,aqua,blue,magenta,red)' }}
          />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <ColorPickerQuickmenu value={pickerValue} onChange={onPickerChange} colorPalette={[]} />
        </PopoverContent>
      </Popover>
    </div>
  )
}
